import React, { useState, useMemo, useCallback } from "react"
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material"
import InvoiceTable from "./invoice-table"
import InvoiceHeader from "./invoice-header"
import InvoicePreview from "./invoice-preview"
import { CreateInvoiceForm } from ".."
import { AddCircleOutline } from "@mui/icons-material"
import { PaymentContainer } from "src/api/modules/container-api"
import moment from "moment"
import { useCreateInvoiceMutation } from "src/api/modules/invoice-api"
import { toast } from "react-toastify"

// --- Types ---
export interface InvoiceRow {
  item?: { value?: string; label?: string }
  unitPrice: string
  units: string
  amount: string
}

interface CreateInvoiceModalProps {
  open: boolean
  onClose: () => void
  invoiceData: CreateInvoiceForm
  selectedPaymentContainers: PaymentContainer[]
  afterSubmitForm: () => void
}

type ModalMode = "edit" | "preview"

// --- Constants ---
const DEFAULT_ROW: InvoiceRow = {
  item: { value: "", label: "" },
  unitPrice: "",
  units: "",
  amount: ""
}

const DIALOG_STYLES = {
  title: { fontWeight: 700, letterSpacing: 1 },
  content: { background: "#f5f7fa", minHeight: 400 },
  actions: { p: 2 },
  previewButton: { minWidth: 140 },
  createButton: { minWidth: 120 }
} as const

// --- Custom Hooks ---
const useInvoiceRows = () => {
  const [rows, setRows] = useState<InvoiceRow[]>([{ ...DEFAULT_ROW }])
  const [touched, setTouched] = useState(false)

  const errors = useMemo(() => rows.map(row => !row.item?.label || !row.unitPrice || !row.units), [rows])

  const totals = useMemo(() => {
    const total = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
    const totalUnitPrice = rows.reduce((sum, row) => sum + (Number(row.unitPrice) || 0), 0)
    return { total, totalUnitPrice }
  }, [rows])

  const handleRowChange = useCallback((idx: number, field: keyof InvoiceRow, value: string) => {
    setRows(prevRows =>
      prevRows.map((row, i) => {
        if (i !== idx) return row

        const newRow = { ...row, [field]: value }

        // Auto-calculate amount when unitPrice or units change
        if (field === "unitPrice" || field === "units") {
          const unitPrice = Number(field === "unitPrice" ? value : row.unitPrice)
          const units = Number(field === "units" ? value : row.units)
          newRow.amount = unitPrice && units ? String(unitPrice * units) : ""
        }

        return newRow
      })
    )
  }, [])

  const handleAddRow = useCallback((idx: number) => {
    setRows(prevRows => {
      const newRows = [...prevRows]
      newRows.splice(idx + 1, 0, { ...DEFAULT_ROW })
      return newRows
    })
  }, [])

  const handleRemoveRow = useCallback((idx: number) => {
    setRows(prevRows => (prevRows.length === 1 ? prevRows : prevRows.filter((_, i) => i !== idx)))
  }, [])

  const resetRows = useCallback(() => {
    setRows([{ ...DEFAULT_ROW }])
    setTouched(false)
  }, [])

  return {
    rows,
    touched,
    errors,
    totals,
    setTouched,
    handleRowChange,
    handleAddRow,
    handleRemoveRow,
    resetRows
  }
}

const useModalMode = () => {
  const [mode, setMode] = useState<ModalMode>("edit")

  const switchToPreview = useCallback(() => setMode("preview"), [])
  const switchToEdit = useCallback(() => setMode("edit"), [])

  return { mode, switchToPreview, switchToEdit }
}

const transformInvoiceData = ({
  rows,
  invoiceData,
  selectedPaymentContainers
}: {
  rows: InvoiceRow[]
  invoiceData: CreateInvoiceForm
  selectedPaymentContainers: PaymentContainer[]
}) => {
  return {
    invoiceNumber: "",
    invoiceDate: invoiceData.invoiceDate ? moment(invoiceData.invoiceDate).toISOString() : undefined,
    dueDate: invoiceData.dueDate ? moment(invoiceData.dueDate).toISOString() : undefined,
    clientName: invoiceData.clientName || "Unknown Client",
    description: invoiceData?.paymentContainerParams?.description?.label || "Invoice for services rendered",
    taxRate: parseFloat(invoiceData?.taxRate?.toString() || "0") || 0,
    status: invoiceData.status as string,
    items: rows.map(row => ({
      itemId: row?.item?.value as string,
      name: row?.item?.label,
      unitPrice: Number(row.unitPrice),
      unit: Number(row.units),
      description: undefined
    })),
    containerIds: selectedPaymentContainers.map(container => container.id)
    // containers: selectedPaymentContainers.map(container => ({
    //   containerId: container.id,
    //   containerNos: container.containerNos,
    //   truck: container.truck,
    //   gd: container.gd,
    //   cnee: container.cnee.name,
    //   term: container.term
    // }))
  }
}
// --- Main Modal Component ---
const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = React.memo(
  ({ open, onClose, invoiceData, selectedPaymentContainers, afterSubmitForm }) => {
    const { rows, touched, errors, totals, setTouched, handleRowChange, handleAddRow, handleRemoveRow, resetRows } =
      useInvoiceRows()

    const { mode, switchToPreview, switchToEdit } = useModalMode()
    const [createInvoice, { isLoading: isCreating }] = useCreateInvoiceMutation()
    const hasErrors = useMemo(() => errors.some(Boolean), [errors])
    const displayErrors = useMemo(() => (touched ? errors : rows.map(() => false)), [touched, errors, rows.length])

    // --- Event Handlers ---
    const handlePreview = useCallback(() => {
      setTouched(true)
      if (hasErrors) return
      switchToPreview()
    }, [setTouched, hasErrors, switchToPreview])

    const handleCreate = useCallback(async () => {
      // TODO: Implement invoice creation logic
      console.log("rows, invoiceData, selectedPaymentContainers", { rows, invoiceData, selectedPaymentContainers })
      const transformedData = transformInvoiceData({ rows, invoiceData, selectedPaymentContainers })
      const resp = await createInvoice(transformedData)
      if (resp.error) {
        toast.error((resp.error as any)?.data?.msg?.[0] || "Error creating invoice")
      } else {
        console.log("Invoice created successfully:", resp.data)
        toast.success("Invoice created successfully")
        window.open(`/invoice/preview/${resp?.data?.data?.id}`, "_blank")
        afterSubmitForm()
        handleClose()
      }
    }, [rows, invoiceData, selectedPaymentContainers])

    const handleClose = useCallback(() => {
      resetRows()
      switchToEdit()
      onClose()
    }, [resetRows, switchToEdit, onClose])

    const handleBackToEdit = useCallback(() => {
      switchToEdit()
    }, [switchToEdit])

    // --- Render Methods ---
    const renderEditMode = () => (
      <>
        <InvoiceHeader invoiceData={invoiceData} />
        <InvoiceTable
          rows={rows}
          onRowChange={handleRowChange}
          onAddRow={handleAddRow}
          onRemoveRow={handleRemoveRow}
          total={totals.total}
          totalUnitPrice={totals.totalUnitPrice}
          errors={displayErrors}
        />
        {touched && hasErrors && (
          <Typography color="error" mt={2} align="center">
            Please fill all required fields in each row.
          </Typography>
        )}
      </>
    )

    const renderPreviewMode = () => (
      <InvoicePreview
        rows={rows}
        total={totals.total}
        totalUnitPrice={totals.totalUnitPrice}
        invoiceData={invoiceData}
      />
    )

    const renderEditActions = () => (
      <>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button variant="contained" onClick={handlePreview} sx={DIALOG_STYLES.previewButton}>
          Preview
        </Button>
      </>
    )

    const renderPreviewActions = () => (
      <>
        <Button onClick={handleBackToEdit} variant="outlined" disabled={isCreating}>
          Back to Edit
        </Button>
        <Button
          variant="contained"
          startIcon={<AddCircleOutline />}
          onClick={handleCreate}
          sx={DIALOG_STYLES.createButton}
          disabled={isCreating}
        >
          Create
        </Button>
      </>
    )

    return (
      <Dialog open={open} maxWidth="md" fullWidth>
        <DialogTitle sx={DIALOG_STYLES.title}>{mode === "edit" ? "Create Invoice" : "Invoice Preview"}</DialogTitle>

        <DialogContent sx={DIALOG_STYLES.content}>
          {mode === "edit" ? renderEditMode() : renderPreviewMode()}
        </DialogContent>

        <DialogActions sx={DIALOG_STYLES.actions}>
          {mode === "edit" ? renderEditActions() : renderPreviewActions()}
        </DialogActions>
      </Dialog>
    )
  }
)

CreateInvoiceModal.displayName = "CreateInvoiceModal"

export default CreateInvoiceModal
